/**
 * Admin V3 — Bookings module — top-level page
 *
 * Mounted at: /admin-v3/bookings (see AdminV3.js)
 *
 * SCOPE FOR PHASE 2.2:
 *   - Read-only: GET /admin/bookings (server-side search/filter/page)
 *   - Per-row click → /admin/bookings/:id (legacy V2 detail with A1+A2 refund)
 *   - Timeline modal renders booking.status_timeline (defensive parsing)
 *   - CSV export: full-dump from /admin/bookings/export/csv
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No POST/PATCH/PUT/DELETE; no idempotency keys; no confirmation modals
 *   - No assign/unassign/auto-assign (Phase 3)
 *
 * Uses V3 foundation hooks: useAdminQuery (auth-memoized, abort-on-unmount).
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import BookingsListSearchBar from './BookingsListSearchBar';
import BookingsListTable from './BookingsListTable';
import BookingTimelineModal from './BookingTimelineModal';
// SC-PR-11: advisory-only refund-guidance modal. Read-only consumer of
// GET /admin/bookings/{id}/refund-recommendation. No POST surface, no
// useAdminMutation, no automatic refund execution.
import RefundGuidanceModal from './RefundGuidanceModal';
// Booking-owned scheduling transition (awaiting_schedule → confirmed).
// Dark — the table only surfaces the action behind VITE_COMMERCIAL_SCHEDULING.
import ScheduleBookingModal from './ScheduleBookingModal';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

const BookingsListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [timelineBooking, setTimelineBooking] = useState(null);
  // SC-PR-11: refund-guidance modal state. Lazily renders the modal
  // and lazily fetches the recommendation only after the operator
  // selects a cancellation reason inside the modal.
  const [refundGuidanceBooking, setRefundGuidanceBooking] = useState(null);
  const [scheduleBooking, setScheduleBooking] = useState(null);

  // Debounce search input (300ms)
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  // Reset page when search/filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter]);

  // Build query params for useAdminQuery
  const queryParams = { limit: PAGE_SIZE, page };
  if (debouncedSearch) queryParams.search = debouncedSearch;
  if (statusFilter !== 'all') queryParams.status = statusFilter;

  const { data, loading, error, refetch } = useAdminQuery('/admin/bookings', queryParams);

  const bookings = Array.isArray(data) ? data : [];

  const isFilterActive = Boolean(debouncedSearch) || statusFilter !== 'all';
  const canGoNext = bookings.length === PAGE_SIZE;
  const canGoPrev = page > 1;
  const pageLabel = t('bookings.page.current', locale).replace('{n}', String(page));

  const handleOpenTimeline = useCallback((booking) => {
    setTimelineBooking(booking);
  }, []);

  // SC-PR-11: open the advisory refund-guidance modal for a given row.
  // The modal handles its own fetch + state internally and never
  // triggers a refund.
  const handleOpenRefundGuidance = useCallback((booking) => {
    setRefundGuidanceBooking(booking);
  }, []);

  return (
    <SectionCard
      title={t('bookings.title', locale)}
      icon={Calendar}
      iconClass="bg-blue-100 text-blue-600"
    >
      <div className="space-y-4">
        <BookingsListSearchBar
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          resultsCount={bookings.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <BookingsListTable
          bookings={bookings}
          loading={loading}
          error={error}
          isFilterActive={isFilterActive}
          onOpenTimeline={handleOpenTimeline}
          onOpenRefundGuidance={handleOpenRefundGuidance}
          onSchedule={setScheduleBooking}
          onRetry={refetch}
          locale={locale}
        />

        {/* Pagination */}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!canGoPrev || loading}
            data-testid="adminv3-bookings-page-prev"
            className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            {t('bookings.page.prev', locale)}
          </Button>
          <span className="text-xs text-slate-500 px-2" data-testid="adminv3-bookings-page-label">
            {pageLabel}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!canGoNext || loading}
            data-testid="adminv3-bookings-page-next"
            className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {t('bookings.page.next', locale)}
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <BookingTimelineModal
        open={Boolean(timelineBooking)}
        onClose={() => setTimelineBooking(null)}
        booking={timelineBooking}
        locale={locale}
      />

      {/*
        SC-PR-11: advisory-only refund-guidance modal. The modal's
        useAdminQuery fetch is gated by both the open state and the
        operator's reason selection. Closing the modal (or unmounting
        because refundGuidanceBooking goes null) tears down the
        AbortController and aborts any in-flight request.
      */}
      <RefundGuidanceModal
        open={Boolean(refundGuidanceBooking)}
        booking={refundGuidanceBooking}
        onClose={() => setRefundGuidanceBooking(null)}
        locale={locale}
      />

      <ScheduleBookingModal
        open={Boolean(scheduleBooking)}
        booking={scheduleBooking}
        onClose={() => setScheduleBooking(null)}
        onScheduled={() => { setScheduleBooking(null); refetch(); }}
        locale={locale}
      />
    </SectionCard>
  );
};

export default BookingsListPage;
